/**
 * Role check via Firebase custom claims.
 *
 * The backend stamps `role: "moderator"` onto a user's Firebase Auth
 * record via the `grant-moderator.go` script (run once per moderator).
 * We don't trust the value here — the backend re-checks the claim on
 * every `/v1/admin/*` request — but reading it client-side lets us:
 *   - Show "Access denied" before making any API call.
 *   - Hide the "Submissions" UI from users who logged in but aren't
 *     moderators (e.g. a regular OpenSoi user who navigated here by
 *     accident).
 */
import type { User } from 'firebase/auth';

export type Role = 'moderator' | 'unauthorized';

/**
 * Read the `role` custom claim from the user's ID token. Forces a
 * refresh on first call so the user doesn't have to sign out/in after
 * being granted moderator access (Firebase caches the ID token for up
 * to 1h by default).
 */
export async function readRole(user: User, forceRefresh = true): Promise<Role> {
  const tokenResult = await user.getIdTokenResult(forceRefresh);
  const claim = tokenResult.claims.role;
  if (typeof claim === 'string' && claim === 'moderator') {
    return 'moderator';
  }
  return 'unauthorized';
}
